import { useContext } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { MockupFramedByCanvasContext } from '@/pages/onboarding/mockup/mockup-canvas-context';
import {
  IPhoneMockup,
  IPhoneMockupProps,
} from '@/pages/onboarding/mockup/iphone-mockup';

// How much bigger the phone gets once zoomed. Deliberately large enough
// that the frame ends up taller than the mockup column itself (chrome bleeds
// off whichever edge is furthest from `zoomOrigin`) — a real "push in" on
// the focal point, not just a slightly-bigger phone. The column
// (OnboardingPageLayout) clips this overflow so it reads as a clean
// zoomed-in crop instead of introducing scroll/spillover.
const DEFAULT_ZOOM_SCALE = 1.55;

// Default pivot: keeps the home screen's app icon roughly anchored as the
// zoom scales up, instead of scaling from dead center — the icon sits at
// ~33% down the wallpaper (home-screen-scene.tsx's ICON_SLOT), so pivoting a
// bit below center pushes the status bar further off the top while keeping
// the icon itself in frame and in evidence. Callers zooming into a different
// focal point (e.g. a tab bar near the bottom of the screen) pass their own
// `zoomOrigin`.
const DEFAULT_ZOOM_ORIGIN = '50% 38%';

const ZOOM_DURATION_S = 0.7;

// Exposed so a page can delay a `navigate()` call by exactly as long as the
// zoom transition it just triggered (zooming in on mount, or zooming back
// out on "Voltar") takes to finish, instead of guessing a number that can
// drift out of sync with the transition below.
export const ZOOM_TRANSITION_DURATION_MS = ZOOM_DURATION_S * 1000;

export interface ZoomedIPhoneMockupProps extends IPhoneMockupProps {
  /**
   * Current target zoom state, fully controlled by the caller. Changing it
   * after mount animates between normal and zoomed size in either
   * direction — e.g. a page starts with `zoomed={false}` and flips to
   * `true` shortly after mount to zoom in, or flips an already-`true` value
   * back to `false` on "Voltar" to play the same animation in reverse
   * before navigating away. A value that's constant across the component's
   * lifetime (e.g. always `true`) never animates — it just renders at that
   * size from first paint, for steps that continue a zoom carried over from
   * a previous step without replaying the entrance.
   */
  zoomed: boolean;
  /**
   * CSS `transform-origin` value the zoom scales from/to, e.g. `'50% 38%'`
   * to pivot on the home screen icon or `'50% 90%'` to pivot on a tab bar
   * near the bottom of the screen. Defaults to the home-icon pivot.
   */
  zoomOrigin?: string;
  /** Scale factor applied when `zoomed` is true. Defaults to 1.55. */
  zoomScale?: number;
  /**
   * Extra vertical shift (CSS `translateY` value, e.g. `'-42%'`) applied
   * together with the scale, when `zoomed` is true. `zoomOrigin` only keeps
   * its own point anchored in place as the frame scales up — it doesn't
   * recenter that point in the mockup column. A focal point that already
   * sits near the column's center (like the home icon) doesn't need this,
   * but one near an edge (like a tab bar close to the bottom) does: this
   * shifts the whole (already-anchored) frame so the focal point actually
   * lands mid-column instead of staying pinned near that edge. Percentage
   * values resolve against the frame's own unscaled height, so the same
   * value works regardless of `zoomScale`. Defaults to no shift.
   */
  zoomTranslateY?: string | number;
}

// Wraps IPhoneMockup with an actual size increase (not a cropped/panned
// close-up) via a CSS scale transform on the whole frame — bezel, notch and
// all grow together, same as physically swapping in a bigger phone.
export function ZoomedIPhoneMockup({
  zoomed,
  zoomOrigin = DEFAULT_ZOOM_ORIGIN,
  zoomScale = DEFAULT_ZOOM_SCALE,
  zoomTranslateY = 0,
  ...mockupProps
}: ZoomedIPhoneMockupProps) {
  const shouldReduceMotion = useReducedMotion();
  // In the mobile Studio the canvas does the zooming (see MockupFocus).
  if (useContext(MockupFramedByCanvasContext)) zoomed = false;
  const translateY = zoomed ? zoomTranslateY : 0;

  if (shouldReduceMotion) {
    return (
      <div
        style={{
          transform: `translateY(${translateY}) scale(${zoomed ? zoomScale : 1})`,
          transformOrigin: zoomOrigin,
        }}
      >
        <IPhoneMockup {...mockupProps} />
      </div>
    );
  }

  return (
    <motion.div
      style={{ transformOrigin: zoomOrigin }}
      initial={{ scale: zoomed ? zoomScale : 1, y: translateY }}
      animate={{ scale: zoomed ? zoomScale : 1, y: translateY }}
      transition={{ duration: ZOOM_DURATION_S, ease: [0.16, 1, 0.3, 1] }}
    >
      <IPhoneMockup {...mockupProps} />
    </motion.div>
  );
}
